import { getLocaleFromI18nLang } from "components/supported-language";
import {
  CountryCallingCode,
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { createTheme } from "@mui/material";
import { error, warning } from "theme/palette";
import { EkycIdentityType } from "services/ekyc-service";
import { CashifyTheme } from "theme";

export type CreateCaseStep = "case" | "person" | "confirm";
export type CaseType = "INDIVIDUAL" | "COMPANY";
export type Gender = "MALE" | "FEMALE";
export type CaseDisplayStatus =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CLOSED"
  | "PENDING_REVIEW";
export type CaseStatus =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CLOSED"
export type CaseStatusAction = "complete" | "close" | "rollback" | "reopen";

export interface IdentityInfo {
  id?: number;
  identityType?: EkycIdentityType;
  identityNumber?: string;
}
export interface PhoneNumberInfo {
  id?: number;
  countryCallingCode?: CountryCallingCode;
  phoneNumber?: string;
}

export interface EmailAddressInfo {
  id?: number;
  emailAddress?: string;
}

export interface AddressInfo {
  id?: number;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  country?: CountryCode; // ISO 3166-1 alpha-2 country code
  province?: string;
}

export interface BasicPersonInfo {
  givenName?: string;
  surname?: string;
  gender?: Gender;
  dateOfBirth?: string; // ISO date string
}
export interface MemberInfo extends BasicPersonInfo {
  emailAddresses: EmailAddressInfo[];
  phoneNumbers: PhoneNumberInfo[];
  identities: IdentityInfo[];
  addresses: AddressInfo[];
}

export interface CreateCaseFormContent {
  caseReference?: string;
  type: CaseType;
  members: MemberInfo[];
}

export const initialMemberInfo: MemberInfo = {
  emailAddresses: [],
  identities: [],
  phoneNumbers: [],
  addresses: [],
};

export interface CountryListItem {
  code: CountryCode;
  callingCode?: string;
  name?: string; // Optional name field for display purposes
}

// Prepare country list
export const getCountryList = (
  lang: string,
  getCallingCode = false
): CountryListItem[] => {
  const locale = getLocaleFromI18nLang(lang);
  const regionNames = new Intl.DisplayNames([locale], { type: "region" });
  if (getCallingCode) {
    return getCountries().map((countryCode) => ({
      code: countryCode,
      callingCode: `+${getCountryCallingCode(countryCode)}`,
      name: regionNames.of(countryCode) || countryCode,
    }));
  } else {
    return getCountries().map((countryCode) => ({
      code: countryCode,
      name: regionNames.of(countryCode) || countryCode,
    }));
  }
};

export const WarningTextFieldTheme = createTheme({
  ...CashifyTheme,
  palette: {
    ...CashifyTheme.palette,
    error: warning,
  },
});

export const ErrorTextFieldTheme = createTheme({
  ...CashifyTheme,
  palette: {
    ...CashifyTheme.palette,
    error: error,
  },
});

export const getLabelFromCaseStatus = (status: CaseDisplayStatus, t: any) => {
  switch (status) {
    case "IN_PROGRESS":
      return t("caseStatus.IN_PROGRESS");
    case "COMPLETED":
      return t("caseStatus.COMPLETED");
    case "CLOSED":
      return t("caseStatus.CLOSED");
    case "PENDING_REVIEW":
      return t("caseStatus.PENDING_REVIEW");
    default:
      return status;
  }
};

export const getColorFromCaseStatus = (status: CaseDisplayStatus) => {
  switch (status) {
    case "IN_PROGRESS":
      return "primary";
    case "COMPLETED":
      return "success";
    case "CLOSED":
      return "grey";
    case "PENDING_REVIEW":
      return "warning";
    default:
      return undefined;
  }
};
